/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useState} from 'react'
import {FormattedMessage} from 'react-intl'
import {
    Box,
    Flex,
    Text,
    Image,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '../../../components/link'
import {HOME_HREF} from '@salesforce/retail-react-app/app/constants'
import {getUrl} from '../../../utils/cloudinary-utils'
import {useUpdateShopperContext} from '../../../hooks/use-update-shopper-context'
import {useGlobaleContext} from '../../../contexts/globale'

const PROGRESSBAR_GRADIENT = 'linear-gradient(270.05deg, #eddeff, #ffe9cb 52.6%, #f1e02b)'
const CheckoutHeader = () => {
    const styles = useMultiStyleConfig('Header')
    const {countryISO} = useGlobaleContext()
    // Handle updating the shopper context (NOTE!! Please keep it inside <GlobaleProvider> because we have to set the globale context)
    useUpdateShopperContext()

    const [progressBarWidth, setProgressBarWidth] = useState('0%')
    useEffect(() => {
        const interval = setInterval(() => {
            if (
                document.querySelector('.checkout-loading-spinner') ||
                document.querySelector('.checkout-header-box')?.getAttribute('data-country') !==
                    'US'
            ) {
                setProgressBarWidth('0%')
                return
            }
            const stepValue = document
                .querySelector('.checkout-container-box')
                ?.getAttribute('data-active-step')
            if (stepValue === '1') {
                setProgressBarWidth('30%')
            } else if (stepValue === '2' || stepValue === '0') {
                setProgressBarWidth('64%')
            } else {
                setProgressBarWidth('78%')
            }
        }, 300)

        return () => clearInterval(interval)
    }, [])

    return (
        <>
            <Box
                padding="15px 20px"
                bg="white"
                className="checkout-header-box"
                data-country={countryISO}
                borderBottom="1px solid"
                borderColor="gray.700"
            >
                <Box
                    maxWidth="container.xxxl"
                    marginLeft="auto"
                    marginRight="auto"
                    className="checkout-header-flex"
                >
                    <Flex align="center" justify="space-between" className="checkout-header-flex">
                        <Link
                            isExternal={true} // Note: Please keep this external until the paypal issue is resolved!
                            to={HOME_HREF}
                            title="Back to homepage"
                            className="header-icon-button header-brand-logo"
                        >
                            <Image
                                {...styles.logo}
                                src={getUrl('UIImages/images/full-ks-logo.svg')}
                                width={{base: '206px', lg: '241px'}}
                                height={{base: '35px', lg: '41px'}}
                                alt="Kendra Scott Logo"
                                verticalAlign="middle"
                            />
                        </Link>

                        <Text
                            color="black.100"
                            fontWeight="400"
                            className="checkout-text"
                            fontFamily="heading"
                            fontSize="18px"
                            display={{base: 'block', lg: 'none'}}
                        >
                            <FormattedMessage
                                id="cart_cta.btn.checkout"
                                defaultMessage="Checkout"
                            />
                        </Text>
                    </Flex>
                </Box>
            </Box>
            <Box
                width={progressBarWidth}
                transition="width, 0.5s ease"
                height="5px"
                className="checkout-progress-bar"
                bg={PROGRESSBAR_GRADIENT}
            />
        </>
    )
}

export default CheckoutHeader
